import React from 'react';
import { Formik, FormikErrors, FormikHelpers } from 'formik';

import { CircularProgress } from '@mui/material';

import styled from 'styled-components';

import { Button } from 'components/Button';

import { EntityModules, ProtectorType } from 'core/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityEnumsOptions } from 'utils/api/modules/entities/entities.types';

import EditEntityRow from 'components/EditEntityRow';

import EntityDropdownField from 'components/SharedFormComponents/EntityDropdownField';

import EntitiesFormField from 'containers/EntitiesFormSubmission/components/EntitiesFormField';

import {
  getPanelGating,
  INITIAL_RISK_FIELD_KEY,
  RESIDUAL_RISK_FIELD_KEY,
  RiskMatrixValue,
  RiskScoringPanel,
  TARGET_RISK_FIELD_KEY,
} from 'components/RiskMatrix';

import { getUserFilter, isFieldRequired, isFieldVisible } from '../EntityDetailsTab/entityFieldConfig';

import { ScoringPanelWrapper } from './styles';
import { transformInitialValues, transformValues } from './utils';
import { EditEntitySectionProps, InitialValues } from './types';

const StyledRow = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const EditEntitySection: React.FunctionComponent<EditEntitySectionProps> = ({
  setEditing,
  fieldOrder,
  store: { edit, details },
  store,
  requiredFieldKeys,
}): React.ReactElement => {
  const entity = details?.entity || {};

  React.useEffect(() => store.trackEditForm(), [store]);

  const { entityFields } = store.store;

  const initialValues = transformInitialValues(entity);

  const { isDrawnByPanel, isPanelAnchor } = getPanelGating(fieldOrder);

  const onSubmit = async (values: InitialValues, actions: FormikHelpers<InitialValues>): Promise<void> => {
    const val = transformValues(values, initialValues, entity, entityFields.data);
    const updated = await edit(val);

    if (typeof updated === 'boolean') {
      actions.setSubmitting(!updated);
      setEditing(!updated);
    } else {
      const errors = updated.reduce((acc, curr) => {
        acc[curr] = store.fieldErrors?.[curr] || 'This field is required';
        return acc;
      }, {} as FormikErrors<InitialValues>);

      actions.setErrors(errors);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ isSubmitting, handleSubmit, setFieldValue, values, errors }) => (
        <form onSubmit={handleSubmit}>
          {fieldOrder.map((outerOrderKey) => {
            if (isDrawnByPanel(outerOrderKey)) {
              // Edited by the panel rendered beside its inherent sibling.
              return null;
            }

            if (isPanelAnchor(outerOrderKey)) {
              const rate =
                (key: string) =>
                (rating: RiskMatrixValue | null): void => {
                  setFieldValue(key, rating);
                  store.contentHasBeenEdited(key, rating as unknown as AnswerTypes);
                };

              return (
                <ScoringPanelWrapper key={`edit-${outerOrderKey}`}>
                  <RiskScoringPanel
                    inherent={values[INITIAL_RISK_FIELD_KEY] as unknown as RiskMatrixValue}
                    residual={values[RESIDUAL_RISK_FIELD_KEY] as unknown as RiskMatrixValue}
                    target={values[TARGET_RISK_FIELD_KEY] as unknown as RiskMatrixValue}
                    onChangeInherent={rate(INITIAL_RISK_FIELD_KEY)}
                    onChangeResidual={rate(RESIDUAL_RISK_FIELD_KEY)}
                    onChangeTarget={rate(TARGET_RISK_FIELD_KEY)}
                  />
                </ScoringPanelWrapper>
              );
            }

            let item = entity[outerOrderKey];

            const entityField = entityFields.getFieldById(outerOrderKey, item?.value);

            if (!entityField) {
              return null;
            }
            item = entityField;

            const { schema, value } = item;

            if (schema.readonly) {
              return null;
            }
            if (!isFieldVisible(store.entityType, outerOrderKey, values)) {
              return null;
            }

            const isRequired =
              isFieldRequired(store.entityType, outerOrderKey) ?? requiredFieldKeys?.has(outerOrderKey) ?? false;
            const serverMessage = errors[outerOrderKey] ? store.fieldErrors?.[outerOrderKey] : undefined;

            const onChange = (val: AnswerTypes): void => {
              setFieldValue(outerOrderKey, val);
              store.contentHasBeenEdited(outerOrderKey, val);
            };

            if (
              schema.protectorType === ProtectorType.MultiChoice ||
              schema.protectorType === ProtectorType.SingleChoice
            ) {
              if (!schema.enum) {
                return null;
              }

              const activeEnums = (schema.enum as unknown as EntityEnumsOptions[]).filter((e) => e.active);

              return (
                <EditEntityRow
                  key={`edit-${outerOrderKey}`}
                  id={`edit-${outerOrderKey}`}
                  inputTestId={`entity-edit-field-${outerOrderKey}`}
                  name={schema.name || 'N/A'}
                  helpText={schema.helpText ?? undefined}
                  required={isRequired}
                >
                  <EntityDropdownField
                    requiredNotFilled={!!errors[outerOrderKey]}
                    items={{ enum: activeEnums }}
                    description={schema.name || 'N/A'}
                    type={schema.protectorType as ProtectorType}
                    onChange={(val) => onChange(val)}
                    value={(values[outerOrderKey] as { key: string; value: string }[]) || ''}
                  />
                </EditEntityRow>
              );
            }

            return (
              <EditEntityRow
                key={`edit-${outerOrderKey}`}
                id={`edit-${outerOrderKey}`}
                inputTestId={`entity-edit-field-${outerOrderKey}`}
                name={schema.name || 'N/A'}
                helpText={schema.helpText ?? undefined}
                required={isRequired}
              >
                <EntitiesFormField
                  type={schema.protectorType as ProtectorType}
                  value={value || ''}
                  onChange={(val: AnswerTypes) => onChange(val)}
                  description={schema.name || 'N/A'}
                  items={(schema.enum || []).map((e) => ({ key: e, value: e }))}
                  requiredNotFilled={!!errors[outerOrderKey]}
                  lookupType={schema.lookupType as EntityModules}
                  errorMessage={serverMessage}
                  userFilter={getUserFilter(store.entityType, outerOrderKey)}
                />
              </EditEntityRow>
            );
          })}

          <StyledRow>
            <Button
              disabled={isSubmitting}
              onClick={() => setEditing(false)}
              style={{
                marginRight: '16px',
              }}
              variant="outlined"
              size="large"
            >
              Cancel
            </Button>
            {isSubmitting ? (
              <Button
                disabled
                startIcon={<CircularProgress size={24} color="inherit" />}
                variant="contained"
                size="large"
              >
                Saving...
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting} variant="contained" size="large">
                Save
              </Button>
            )}
          </StyledRow>
        </form>
      )}
    </Formik>
  );
};
export default EditEntitySection;
